/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AlertConfigRequest, AlertConfigResponse, ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

type CreateAlertConfigMutationConfig = {
  onSuccess?: (data: AlertConfigResponse) => void;
  onError?: (error: ApiError) => void;
};

export function useCreateAlertConfigMutation({ onSuccess, onError }: CreateAlertConfigMutationConfig = {}) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<AlertConfigResponse, ApiError, AlertConfigRequest>({
    mutationFn: (requestBody) => {
      return mgmtPlaneAPIClient.alerts.createAlertConfig({
        tenantId: selectedTenantId!,
        requestBody,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['alertConfigs'] });
      onSuccess?.(data);
    },
    onError,
  });

  return { mutate, isError, error, isPending };
}
